import { useEffect, useState } from 'react';
import Modal from '../Modal';
import { repositorioCliente } from '../../sync/repositories/financeiro';
import { extrairMensagensErro } from '../../utils/errorUtils';

const formatarCnpj = (value) => {
  if (!value) return '';
  const cnpj = value.replace(/\D/g, '');
  if (cnpj.length <= 14) {
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
};

const formatarCpf = (value) => {
  if (!value) return '';
  const cpf = value.replace(/\D/g, '');
  if (cpf.length <= 11) {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return value;
};

const ESTADO_INICIAL = {
  tipo_pessoa: 'J',
  razao_social: '',
  cnpj: '',
  nome_completo: '',
  cpf: '',
};

/**
 * Modal de cadastro rápido de Cliente. Mesma estrutura de
 * ModalCadastroFornecedor — Cliente e Fornecedor têm o mesmo formato de
 * pessoa (jurídica/física) no backend, só mudam de model/endpoint.
 */
export default function ModalCadastroCliente({ isOpen, onClose, onSuccess }) {
  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setDados(ESTADO_INICIAL);
      setErros([]);
      setSalvando(false);
    }
  }, [isOpen]);

  const validar = () => {
    if (dados.tipo_pessoa === 'J') {
      if (!dados.razao_social.trim()) return 'A razão social é obrigatória.';
      if (dados.cnpj.replace(/\D/g, '').length !== 14) return 'Informe um CNPJ válido (14 dígitos).';
    } else {
      if (!dados.nome_completo.trim()) return 'O nome completo é obrigatório.';
      if (dados.cpf.replace(/\D/g, '').length !== 11) return 'Informe um CPF válido (11 dígitos).';
    }
    return null;
  };

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    const mensagem = validar();
    if (mensagem) { setErros([mensagem]); return; }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioCliente.criar({
        tipo_pessoa: dados.tipo_pessoa,
        razao_social: dados.tipo_pessoa === 'J' ? dados.razao_social.trim() : '',
        cnpj: dados.tipo_pessoa === 'J' ? dados.cnpj : '',
        nome_completo: dados.tipo_pessoa === 'F' ? dados.nome_completo.trim() : '',
        cpf: dados.tipo_pessoa === 'F' ? dados.cpf : '',
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Cliente">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              checked={dados.tipo_pessoa === 'J'}
              onChange={() => setDados((prev) => ({ ...prev, tipo_pessoa: 'J' }))}
              disabled={salvando}
              className="text-green-600 focus:ring-green-500"
            />
            Pessoa Jurídica
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="radio"
              checked={dados.tipo_pessoa === 'F'}
              onChange={() => setDados((prev) => ({ ...prev, tipo_pessoa: 'F' }))}
              disabled={salvando}
              className="text-green-600 focus:ring-green-500"
            />
            Pessoa Física
          </label>
        </div>

        {dados.tipo_pessoa === 'J' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
              <input
                type="text"
                value={dados.razao_social}
                onChange={(e) => setDados((prev) => ({ ...prev, razao_social: e.target.value }))}
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
                autoFocus
                disabled={salvando}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
              <input
                type="text"
                value={formatarCnpj(dados.cnpj)}
                onChange={(e) => setDados((prev) => ({ ...prev, cnpj: e.target.value.replace(/\D/g, '') }))}
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
                maxLength={18}
                disabled={salvando}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
              <input
                type="text"
                value={dados.nome_completo}
                onChange={(e) => setDados((prev) => ({ ...prev, nome_completo: e.target.value }))}
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
                autoFocus
                disabled={salvando}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
              <input
                type="text"
                value={formatarCpf(dados.cpf)}
                onChange={(e) => setDados((prev) => ({ ...prev, cpf: e.target.value.replace(/\D/g, '') }))}
                className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
                maxLength={14}
                disabled={salvando}
              />
            </div>
          </>
        )}

        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
